import Konva from 'konva';
import EventView from "./konva_components/EventView.js";
import JugglerView from "./konva_components/JugglerView.js";
import Pattern from "./logical_pattern/Pattern.js";
import Rhythm from "./logical_pattern/Rhythm.js";
import PlacementManager from "./PlacementManager.js";
import SnappingManager from "./SnappingManager.js";

export default class CausalView {
  constructor(containerID) {
    this.snapManager = new SnappingManager();
    this.pattern = new Pattern();
    this.placementManager = new PlacementManager(this.pattern, this.stage, this.layer);
    this.jugglerViews = [];
    this.eventViews = new Map();
    this.onChangeCallback = () => {};
    this.onJugglerDoubleClickCallback = () => {};
    this.onEventDoubleClickCallback = () => {};
    this.initializeComponents(containerID);
  }

  initializeComponents(containerID) {
    this.stage = new Konva.Stage({ container: containerID });
    this.layer = new Konva.Layer();
    this.stage.add(this.layer);
    this.background = new Konva.Rect({
      x: 0,
      y: 0,
      fill: 'white',
      listening: false, // Prevent it from capturing events
    });
    this.layer.add(this.background);
    this.background.moveToBottom();
    this.siteswapText = new Konva.Text({
      x: 10,
      y: 10,
      text: "",
      fontSize: 30,
      fontFamily: 'Calibri',
      fill: 'grey',
      align: "center",
    });
    this.layer.add(this.siteswapText);
    this.resize();
  }

  onChange(callback) {
    this.onChangeCallback = callback;
  }

  onJugglerDoubleClick(callback) {
    this.onJugglerDoubleClickCallback = callback;
  }

  onEventDoubleClick(callback) {
    this.onEventDoubleClickCallback = callback;
  }

  resize() {
    this.stage.width(this.stage.getContainer().clientWidth);
    this.stage.height(this.stage.getContainer().clientHeight);
    this.background.width(this.stage.width());
    this.background.height(this.stage.height());
    this.placementManager.width(this.stage.width());
    this.placementManager.height(this.stage.height());
    this.snapManager.setWidth(this.placementManager.viewWidth);
    this.siteswapText.width(this.stage.width());
    this.updateDiagram();
  }

  onEventChanged(event) {
    const fromPatternEvent = this.placementManager.pointToEvent(event.from);
    if (event.snapped) {
      const toPatternEvent = this.placementManager.pointToEvent(event.snapped);
      this.pattern.addThrow(fromPatternEvent, toPatternEvent);
    } else {
      this.pattern.removeThrow(fromPatternEvent);
    }
    this.pattern.updateEventLinks();
    this.updateDiagram();
    this.onChangeCallback();
  }

  fromJIF(jif) {
    this.pattern.fromJIF(jif);
    this.updateDiagram();
    this.onChangeCallback();
  }

  toJIF() {
    return this.pattern.toJIF();
  }

  fromSiteswap(siteswap) {
    this.pattern.fromSiteswap(siteswap);
    this.updateDiagram();
    this.onChangeCallback();
  }

  get siteswap() {
    return this._siteswap;
  }

  addBeat() {
    this.pattern.addBeat();
    this.updateDiagram();
    this.onChangeCallback();
  }

  removeBeat() {
    this.pattern.removeBeat();
    this.updateDiagram();
    this.onChangeCallback();
  }

  setNumBeats(beats) {
    while (this.pattern.numBeats < beats) {
      this.pattern.addBeat();
    }
    while (this.pattern.numBeats > beats) {
      this.pattern.removeBeat();
    } 
    this.updateDiagram();
    this.onChangeCallback();
  }

  addJuggler() {
    this.pattern.addJuggler();
    this.updateDiagram();
    this.onChangeCallback();
  }

  removeJuggler() {
    this.pattern.removeJuggler();
    this.updateDiagram();
    this.onChangeCallback();
  }

  removeEvent(event) {
    this.pattern.jugglers[event.juggler].rhythm = Rhythm.CUSTOM;
    this.pattern.removeEvent(event);
    this.pattern.updateEventLinks();
    this.updateDiagram();
    this.onChangeCallback();
  }

  updateEvent(event, update) {
    if (event.time != update.time || event.juggler != update.juggler || event.hand != update.hand) {
      // If anything changed about placement, set juggler rhythm to CUSTOM
      this.pattern.jugglers[event.juggler].rhythm = Rhythm.CUSTOM;
    }
    const origDuration = event.duration;
    this.pattern.setEventTime(event, update.time);
    // Only update duration if it didn't change to avoid overriding auto-update from setting event time
    if (origDuration != update.duration) {
      event.duration = update.duration;
    }
    event.juggler = update.juggler;
    event.hand = update.hand;
    event.toJuggler = update.toJuggler;
    event.toHand = update.toHand;
    this.pattern.updateEventLinks();
    this.updateDiagram();
    this.onChangeCallback();
  }

  setJugglerRhythm(juggler, rhythm) {
    this.pattern.setJugglerRhythm(juggler, rhythm);
    this.updateDiagram();
    this.onChangeCallback();
  }

  setNumJugglers(jugglers) {
    while (this.pattern.numJugglers < jugglers) {
      this.pattern.addJuggler();
    }
    while (this.pattern.numJugglers > jugglers) {
      this.pattern.removeJuggler();
    }
    this.updateDiagram();
    this.onChangeCallback();
  }

  renameJuggler(juggler, newId) {
    this.pattern.renameJuggler(juggler, newId);
    this.updateDiagram();
    this.onChangeCallback();
  }

  setJugglerDelay(juggler, delay) {
    this.pattern.setJugglerDelay(juggler, delay);
    this.updateDiagram();
    this.onChangeCallback();
  }

  updateJuggler(juggler, update) {
    juggler.lookAt = update.lookAt;
    juggler.position = update.position;
    juggler.name = update.name;
    this.pattern.setJugglerRhythm(juggler.id, update.rhythm);
    this.pattern.setJugglerDelay(juggler.id, update.delay);
    this.updateDiagram();
    this.onChangeCallback();
  }

  updateDiagram() {
    // NOTE: This is a very inefficient way to update the diagram. No performance issues yet, but it could be a problem.
    this.snapManager.clearSnappableObjects();
    this.eventViews.forEach(eventView => eventView.destroy());
    this.eventViews.clear();
    this.jugglerViews.forEach(jugglerView => jugglerView.destroy());
    this.jugglerViews = [];
    this.layer.clear();

    this.placementManager.calculatePixelMappings();
    this.snapManager.setWidth(this.placementManager.viewWidth);
    const eventPositions = this.placementManager.calculateEventPositions();
    for (const event of this.pattern.events) {
      const eventView = new EventView(
        this.pattern.numBeats,
        this.placementManager.pixelsPerBeat,
        this.stage,
        this.snapManager,
        this.onEventChanged.bind(this),
        eventView => this.onEventDoubleClickCallback(this.placementManager.pointToEvent(eventView.position())),
      );
      eventView.position(eventPositions[event.id()]);
      eventView.hand(event.hand);
      eventView.label(event.getLabel(this.pattern.numJugglers));
      eventView.valid(event.isValid());
      this.layer.add(eventView);
      this.eventViews.set(event.id(), eventView);
    }

    // The snapping requires all the event views to be created first.
    for (const event of this.pattern.events) {
      if (event.toEvent !== undefined) {
        const eventView = this.eventViews.get(event.id());
        const endPos = this.placementManager.getThrowEndPoint(event);
        eventView.setArrowMidpoints(this.placementManager.calculateArrowMidpoints(event, endPos));
        eventView.setEndPoint(endPos);
      }
    }

    // Do juggler labels last so they're on top
    const jugglerPositions = this.placementManager.calculateJugglerPositions();
    for (let i = 0; i < this.pattern.numJugglers; i++) {
      const jugglerView = new JugglerView(i, id => this.onJugglerDoubleClickCallback(this.pattern.jugglers[id]));
      jugglerView.name(this.pattern.jugglers[i].name)
      jugglerView.position(jugglerPositions[i]);
      this.layer.add(jugglerView);
      this.jugglerViews.push(jugglerView);
    }

    this._siteswap = "";
    try {
      this._siteswap = this.pattern.toSiteswap();
    } catch (error) { }
    this.siteswapText.text(this._siteswap);

    this.layer.draw();
  }
}
